"use client";

import { useEffect, useState } from "react";
import { useCategoryStore } from "@/stores/categoryStore";
import {
  UpdateCategoryDto,
  CreateCategoryDto,
  categoryService,
  Category,
} from "@/services/categoryService";
import { showToast } from "@/lib/toast";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface ModalEditCategoryProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string | null;
  onComplete: () => void;
}

export default function ModalEditCategory({
  isOpen,
  onClose,
  categoryId,
  onComplete,
}: ModalEditCategoryProps) {
  const { fetchCategoryById, updateCategory } = useCategoryStore();
  const [roots, setRoots] = useState<Category[]>([]);
  const [form, setForm] = useState<UpdateCategoryDto>({
    name: "",
    slug: "",
    parent: undefined,
    description: "",
    keyword: "",
    seoTitle: "",
    seoDescription: "",
    canonicalUrl: "",
  });
  const [errors, setErrors] = useState<Partial<CreateCategoryDto>>({});
  const [loading, setLoading] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [canonicalTouched, setCanonicalTouched] = useState(false);

  const slugify = (input: string): string =>
    input
      .toLowerCase()
      .normalize("NFD")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "d")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  useEffect(() => {
    const load = async () => {
      if (isOpen && categoryId) {
        setLoading(true);
        const data = await fetchCategoryById(categoryId);
        const res = await categoryService.findAll({ page: 1, limit: 100 });
        if (res.ok && res.data) {
          const data: any = res.data.data;

          const categories = data?.data?.categories || [];
          setRoots(categories.filter((c: Category) => c._id !== categoryId));
        }
        if (data) {
          setForm({
            name: data.name,
            slug: data.slug,
            parent: (data.parent as any)?._id || undefined,
            description: data.description || "",
            keyword: (data as any).keyword || "",
            seoTitle: (data as any).seoTitle || "",
            seoDescription: (data as any).seoDescription || "",
            canonicalUrl: (data as any).canonicalUrl || "",
          });
        }
        setSlugTouched(false);
        setCanonicalTouched(false);
        setLoading(false);
      }
    };
    load();
  }, [isOpen, categoryId, fetchCategoryById]);

  const validate = () => {
    const e: Partial<CreateCategoryDto> = {};
    if (!form.name?.trim()) e.name = "Tên danh mục không được để trống";
    if (!form.slug?.trim()) e.slug = "Slug không được để trống";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!categoryId) return;
    if (!validate()) return;
    const ok = await updateCategory(categoryId, form);
    if (ok) {
      showToast.success("Thành công", "Cập nhật danh mục thành công");
      onComplete();
      onClose();
    } else {
      showToast.error("Lỗi", "Không thể cập nhật danh mục");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
            Chỉnh sửa danh mục
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="edit-name"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Tên danh mục
                </Label>
                <Input
                  id="edit-name"
                  value={form.name || ""}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setForm((p) => ({
                      ...p,
                      name: newName,
                    slug: slugTouched ? p.slug : slugify(newName),
                    canonicalUrl: canonicalTouched ? (p as any).canonicalUrl : slugify(newName),
                    }));
                  }}
                  className={errors.name ? "border-red-500" : ""}
                  disabled={loading}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="edit-slug"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Slug
                </Label>
                <Input
                  id="edit-slug"
                  value={form.slug || ""}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm((p) => ({ ...p, slug: e.target.value }));
                  }}
                  className={errors.slug ? "border-red-500" : ""}
                  disabled={loading}
                />
                {errors.slug && (
                  <p className="text-red-500 text-sm">{errors.slug}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Danh mục cha
                </Label>
                <Select
                  value={form.parent || "none"}
                  onValueChange={(v: string) =>
                    setForm((p) => ({
                      ...p,
                      parent: v === "none" ? undefined : v,
                    }))
                  }
                  disabled={loading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Chọn danh mục cha" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Không chọn</SelectItem>
                    {roots.map((r) => (
                      <SelectItem key={r._id} value={r._id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả
                </label>
                <textarea
                  value={form.description || ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 min-h-[90px]"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Canonical URL (tùy chọn)
                </label>
                <input
                  value={(form as any).canonicalUrl || ""}
                  onChange={(e) => {
                    setCanonicalTouched(true);
                    setForm((p) => ({ ...p, canonicalUrl: e.target.value }));
                  }}
                  placeholder="duong-dan-chuan"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                  type="text"
                  inputMode="text"
                  disabled={loading}
                />
              </div>

              {/* SEO fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Keyword
                  </label>
                  <input
                    value={form.keyword || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, keyword: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SEO Title
                  </label>
                  <input
                    value={form.seoTitle || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, seoTitle: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SEO Description
                </label>
                <textarea
                  value={form.seoDescription || ""}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, seoDescription: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 min-h-[100px]"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Hủy
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Đang lưu...
                </span>
              ) : (
                "Lưu thay đổi"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
